<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import type { Snippet } from "svelte"
  import { displayContactChannelLabel } from "../../../../../../../../../_shared/_logic/outreach-presentation"
  import { resolveTemplateDefaults } from "../../../../../../../_shared/_logic/outreach-template-defaults"
  import {
    SMS_SINGLE_SEGMENT_LIMIT,
    TEMPLATE_HELPER_TEXT,
    buildSmsLengthMessage,
    emailBodyTemplateTokens,
    emailSubjectTemplateTokens,
    smsTemplateTokens,
  } from "../../../../../../../_shared/_logic/template-tokens"
  import { createContactOrderStep } from "../../../../../../../../../api/kehrblatt/contact-orders"
  import { fetchContactPolicy } from "../../../../../../../../../api/kehrblatt/contact-policy"
  import StepEditorSaveAction from "./_modules/step-editor-save-action"
  import type {
    ContactOrderStepOverlayProps,
    ContactStepSelectedTargetInfo,
    ContactStepTemplateVariant,
  } from "./_shared/_logic/step-editor-contract"
  import {
    buildInitialEditorState,
    buildStepTemplateSelection,
    defaultSubjectForChannel,
    defaultTemplateForChannel,
    resolveContactStepTemplateVariant,
  } from "./_logic/editorState"
  import {
    buildCustomerById,
    buildTargetOptions,
    sanitizeTargets,
    splitContactTargets,
  } from "./_logic/targets"
  import { computeMinDue, roundUpToQuarter } from "./_logic/time"

  const {
    mode,
    caseId = "",
    contactOrderId,
    order = null,
    step = null,
    availableChannels = [],
    contactPoints = [],
    customers = [],
    minHoursAhead = 2,
    organizationId,
    onCreated,
    onSave,
    onClose,
    setActionButtonSnippet = null,
  }: ContactOrderStepOverlayProps = $props()

  const minHours = $derived(Math.max(minHoursAhead ?? 2, 2))
  const minDue = $derived(computeMinDue(minHours))
  const customerById = $derived(buildCustomerById(customers))
  const targetPools = $derived(splitContactTargets(contactPoints))
  const sourceTemplateVariant = $derived(resolveContactStepTemplateVariant({ step, order }))

  const channelSupportsContactPoints = (channel: ContactChannel) => {
    if (channel === "EMAIL") return targetPools.emailTargets.length > 0

    if (channel === "MOBILE_MESSAGE") return targetPools.mobilePhoneTargets.length > 0

    if (channel === "PHONE") return targetPools.phoneTargets.length > 0

    return true
  }

  const SAFE_FALLBACK_CHANNELS = ["EMAIL"] as ContactChannel[]
  const DIGITAL_EDITOR_CHANNELS = new Set<ContactChannel>(["EMAIL", "MOBILE_MESSAGE", "PHONE"])

  const channelAllowedForVariant = (
    channel: ContactChannel,
    variant: ContactStepTemplateVariant,
  ) => {
    if (!DIGITAL_EDITOR_CHANNELS.has(channel)) return false

    if (variant === "follow_up") return channel === "EMAIL"

    return true
  }

  const baseChannels = $derived(
    availableChannels.length ? availableChannels : SAFE_FALLBACK_CHANNELS,
  )

  const allowedChannelSet = $derived(new Set<ContactChannel>(baseChannels))

  const normalizedChannels = $derived.by(() => {
    const set = new Set<ContactChannel>()
    for (const channel of baseChannels) {
      if (channelAllowedForVariant(channel, sourceTemplateVariant)) set.add(channel)
    }

    if (
      targetPools.emailTargets.length &&
      allowedChannelSet.has("EMAIL") &&
      channelAllowedForVariant("EMAIL", sourceTemplateVariant)
    ) {
      set.add("EMAIL")
    }

    if (
      targetPools.phoneTargets.length &&
      allowedChannelSet.has("PHONE") &&
      channelAllowedForVariant("PHONE", sourceTemplateVariant)
    ) {
      set.add("PHONE")
    }

    if (
      targetPools.mobilePhoneTargets.length &&
      allowedChannelSet.has("MOBILE_MESSAGE") &&
      channelAllowedForVariant("MOBILE_MESSAGE", sourceTemplateVariant)
    ) {
      set.add("MOBILE_MESSAGE")
    }

    return Array.from(set)
  })

  const filteredChannels = $derived.by(() => {
    const valid = normalizedChannels.filter((channel) => channelSupportsContactPoints(channel))
    const current = step?.channel as ContactChannel | undefined

    return current &&
      channelAllowedForVariant(current, sourceTemplateVariant) &&
      !valid.includes(current)
      ? [...valid, current]
      : valid
  })

  const channelUnavailableReason = (channel: ContactChannel) => {
    const label = displayContactChannelLabel(channel)

    if (channel === "EMAIL")
      return `${label} nicht verfügbar: keine E-Mail-Kontaktpunkte hinterlegt.`

    if (channel === "MOBILE_MESSAGE") {
      return `${label} nicht verfügbar: keine mobile Telefonnummer hinterlegt.`
    }

    if (channel === "PHONE") return `${label} nicht verfügbar: keine Telefonnummern hinterlegt.`

    return `${label} nicht verfügbar.`
  }

  const unavailableChannelReasons = $derived.by(() =>
    baseChannels
      .filter((channel) => channelAllowedForVariant(channel, sourceTemplateVariant))
      .filter((channel) => !filteredChannels.includes(channel))
      .map(channelUnavailableReason),
  )

  const channelTooltip = $derived(
    unavailableChannelReasons.length ? unavailableChannelReasons.join("\n") : "",
  )

  let localChannel = $state<ContactChannel | "">("")
  let localContactPoint = $state<string>("")
  let localDue = $state<string>("")
  let localTitle = $state<string>("")
  let localNotes = $state<string>("")
  let localTemplateBody = $state<string>("")
  let localTemplateSubject = $state<string>("")
  let localTemplateVariant = $state<ContactStepTemplateVariant>("announcement")
  let saving = $state(false)
  let templateDefaults = $state(resolveTemplateDefaults())
  const smsLengthState = $derived(buildSmsLengthMessage(localTemplateBody))

  const hydrateFromProps = () => {
    const state = buildInitialEditorState({
      mode,
      step,
      order,
      filteredChannels,
      minDue,
      targetPools,
      templateDefaults,
    })
    localChannel = state.channel
    localContactPoint = state.contactPointId
    localDue = state.due
    localTitle = state.title
    localNotes = state.notes
    localTemplateBody = state.templateBody
    localTemplateSubject = state.templateSubject
    localTemplateVariant = state.templateVariant
  }

  const loadPolicyTemplates = async () => {
    if (!organizationId) return

    try {
      const policy = await fetchContactPolicy(organizationId)
      const previousBodyDefault = defaultTemplateForChannel(
        localChannel,
        templateDefaults,
        localTemplateVariant,
      )
      const previousSubjectDefault = defaultSubjectForChannel(
        localChannel,
        templateDefaults,
        localTemplateVariant,
      )
      const shouldRefreshBody = !localTemplateBody || localTemplateBody === previousBodyDefault
      const shouldRefreshSubject =
        !localTemplateSubject || localTemplateSubject === previousSubjectDefault
      const nextTemplateDefaults = resolveTemplateDefaults(
        policy.templates as Record<string, unknown>,
      )
      templateDefaults = nextTemplateDefaults

      if (shouldRefreshBody) {
        localTemplateBody =
          defaultTemplateForChannel(localChannel, nextTemplateDefaults, localTemplateVariant) || ""
      }

      if (shouldRefreshSubject) {
        localTemplateSubject =
          defaultSubjectForChannel(localChannel, nextTemplateDefaults, localTemplateVariant) || ""
      }
    } catch (err) {
      console.error("contact_step_templates_load_failed", err)
    }
  }

  onMount(() => {
    setActionButtonSnippet?.(actionButtons as Snippet)
    hydrateFromProps()

    if (organizationId) {
      void loadPolicyTemplates()
    }
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)
  })

  const syncContactPointWithChannel = (channel: ContactChannel | "") => {
    const sanitized = sanitizeTargets(
      channel,
      localContactPoint ? [localContactPoint] : [],
      targetPools,
    )

    if (sanitized.length) {
      localContactPoint = sanitized[0]

      return
    }

    localContactPoint = ""
  }

  const handleChannelChange = (channel: ContactChannel | "") => {
    localChannel = channel
    syncContactPointWithChannel(channel)
    localTemplateBody =
      defaultTemplateForChannel(channel, templateDefaults, localTemplateVariant) || ""
    localTemplateSubject =
      defaultSubjectForChannel(channel, templateDefaults, localTemplateVariant) || ""
  }

  const normalizeDue = () => {
    localDue = roundUpToQuarter(localDue) || ""
  }

  const targetOptions = $derived(buildTargetOptions(localChannel, targetPools, customerById))

  const selectedTargetInfo = $derived.by((): ContactStepSelectedTargetInfo | null => {
    if (!localContactPoint) return null

    const option = targetOptions.find((entry) => entry.value === localContactPoint)

    return {
      id: localContactPoint,
      label:
        option?.name ||
        (step?.destination?.contact_point_id === localContactPoint
          ? step.destination.value
          : localContactPoint),
      reason: null,
    }
  })

  const closeOverlay = () => {
    onClose?.()
    window.dispatchEvent(new CustomEvent("overlay.close"))
  }

  const handleSave = async () => {
    if (!localChannel) {
      newNotification({ class: "error", html: "Bitte einen Kanal auswählen." })

      return
    }

    if (localChannel === "MOBILE_MESSAGE" && !smsLengthState.withinLimit) {
      newNotification({
        class: "error",
        html: `Mobile-Nachricht-Text ist zu lang. Erlaubt sind geschaetzt maximal ${SMS_SINGLE_SEGMENT_LIMIT} Zeichen.`,
      })

      return
    }

    const parsedDue = new Date(localDue)

    if (Number.isNaN(parsedDue.getTime())) {
      newNotification({ class: "error", html: "Bitte ein gültiges Datum wählen." })

      return
    }

    if (parsedDue < minDue) {
      newNotification({
        class: "error",
        html: `Geplante Zeit muss mindestens ${minHours} Stunden in der Zukunft liegen.`,
      })
      localDue = roundUpToQuarter(minDue) || ""

      return
    }

    saving = true
    try {
      const dueIso = parsedDue.toISOString()
      const channelNeedsTarget =
        localChannel === "EMAIL" || localChannel === "PHONE" || localChannel === "MOBILE_MESSAGE"

      if (channelNeedsTarget && !localContactPoint) {
        newNotification({ class: "error", html: "Bitte einen Kontaktwert auswählen." })
        saving = false

        return
      }

      const selectedPoint = contactPoints.find((point) => point.id === localContactPoint)
      const recipientCustomerId =
        selectedPoint?.customer_id ??
        (step?.destination?.contact_point_id === localContactPoint
          ? step.recipient_customer_id
          : null)

      if (channelNeedsTarget && !recipientCustomerId) {
        newNotification({
          class: "error",
          html: "Der ausgewählte Kontaktwert ist keinem Empfänger zugeordnet.",
        })

        return
      }

      const templateSelection = buildStepTemplateSelection(
        localChannel,
        localTemplateVariant,
        localTemplateBody,
        localTemplateSubject,
      )

      if (mode === "edit") {
        await onSave?.({
          channel: localChannel,
          contact_point_id: localContactPoint || null,
          recipient_customer_id: recipientCustomerId,
          execution_mode: step?.execution_mode ?? "automatic_sequence",
          automatic_delivery_committed: step?.automatic_delivery_committed ?? false,
          retries_on_no_answer: step?.retries_on_no_answer ?? 1,
          due_at: dueIso,
          title: localTitle,
          notes: localNotes,
          ...templateSelection,
        })
        newNotification({ class: "success", html: "Schritt gespeichert." })
      } else {
        if (!contactOrderId) {
          newNotification({ class: "error", html: "Kein Kundenkontakt ausgewählt." })

          return
        }

        const created = await createContactOrderStep(caseId, contactOrderId, {
          channel: localChannel,
          execution_mode: "automatic_sequence",
          automatic_delivery_committed: false,
          contact_point_id: localContactPoint || null,
          recipient_customer_id: recipientCustomerId,
          due_at: dueIso,
          title: localTitle || null,
          notes: localNotes || null,
          ...templateSelection,
        })
        newNotification({ class: "success", html: "Schritt hinzugefügt." })
        await onCreated?.(created)
      }

      closeOverlay()
    } catch (err) {
      console.error("contact_order_step_save_failed", err)
      newNotification({ class: "error", html: "Schritt konnte nicht gespeichert werden." })
    } finally {
      saving = false
    }
  }
</script>

<div class="form light">
  <div class="row">
    <Input
      type="select"
      placeholder="Kanal"
      bind:value={localChannel}
      options={filteredChannels.map((channel) => ({
        name: displayContactChannelLabel(channel),
        value: channel,
      }))}
      brightBackground={true}
      tooltip={channelTooltip || null}
      onChange={() => handleChannelChange(localChannel)}
    />
    <Input
      type="datetime-local"
      placeholder="Geplante Zeit"
      bind:value={localDue}
      brightBackground={true}
      onChange={normalizeDue}
    />
  </div>
  <div class="row">
    {#if targetOptions.length}
      <div class="target-selection">
        <Input
          type="select"
          placeholder={localChannel === "EMAIL" ? "Empfänger (E-Mail)" : "Rufnummer"}
          options={targetOptions}
          bind:value={localContactPoint}
          helperText={localChannel === "EMAIL"
            ? "Wählen Sie die Person, die in diesem Schritt per E-Mail kontaktiert werden soll."
            : localChannel === "MOBILE_MESSAGE"
              ? "Wählen Sie die mobile Rufnummer, die in diesem Schritt verwendet werden soll."
              : "Wählen Sie die Rufnummer, die in diesem Schritt verwendet werden soll."}
          brightBackground={true}
          onChange={() => syncContactPointWithChannel(localChannel)}
        />
      </div>
    {/if}
  </div>

  <Input placeholder="Notiz" bind:value={localTitle} brightBackground={true} />

  {#if localChannel === "EMAIL"}
    <Input
      type="tagbased_input"
      placeholder="E-Mail Betreff"
      bind:value={localTemplateSubject}
      options={emailSubjectTemplateTokens}
      tokenSyntax="single"
      brightBackground={true}
      helperText={TEMPLATE_HELPER_TEXT}
    />
    <Input
      type="tagbased_input"
      placeholder="E-Mail Text"
      bind:value={localTemplateBody}
      options={emailBodyTemplateTokens}
      tokenSyntax="single"
      brightBackground={true}
      helperText={TEMPLATE_HELPER_TEXT}
    />
  {:else if localChannel === "MOBILE_MESSAGE"}
    <Input
      type="tagbased_input"
      placeholder="Text für mobile Nachricht"
      bind:value={localTemplateBody}
      options={smsTemplateTokens}
      tokenSyntax="single"
      brightBackground={true}
      helperText={`${smsLengthState.helperText} ${TEMPLATE_HELPER_TEXT}`}
    />
  {/if}
</div>

{#snippet actionButtons()}
  <StepEditorSaveAction
    {mode}
    {saving}
    disabled={localChannel === "MOBILE_MESSAGE" && !smsLengthState.withinLimit}
    onSave={handleSave}
  />
{/snippet}

<style lang="less">
  .form {
    height: fit-content;
  }

  .target-selection {
    display: grid;
    gap: 0.7rem;
    width: 100%;
  }

  .target-preview {
    display: grid;
    gap: 0.45rem;
    padding: 0.8rem 0.9rem;
    border: 1px solid color-mix(in srgb, var(--border-color) 72%, transparent);
    background: color-mix(in srgb, var(--background-color) 92%, black);
  }

  .target-preview-title {
    font-size: 0.78rem;
    text-transform: uppercase;
    color: color-mix(in srgb, var(--text-100) 62%, transparent);
  }

  .target-preview-row {
    display: grid;
    gap: 0.2rem;
  }

  .target-preview-label {
    color: var(--text-100);
  }

  .target-preview-reason {
    font-size: 0.85rem;
    color: color-mix(in srgb, var(--text-100) 68%, transparent);
  }
</style>
